import React, { useState, useRef, useEffect } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { Tooltip } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import GenericButton from 'components/Button/GenericButton'
import { Icon } from 'packages/eid-icons'
import { useTheme } from '@mui/material/styles'

const FileUploadContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
})

const FileInputWrapper = styled(Box)(({ hasError }) => ({
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${hasError ? '#ff4d5f' : '#d8d8dd'}`,
    borderRadius: '5px',
    backgroundColor: '#ffffff',
    boxShadow: hasError
        ? '0 2px 4px 0 rgba(255, 77, 95, 0.15)'
        : '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    flex: 1,
    minHeight: '40px',
}))

const FileNameDisplay = styled(Box)({
    flex: 1,
    padding: '8px 12px',
    color: '#8b909a',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0, // Important for flex truncation
})

const HiddenFileInput = styled('input')({
    display: 'none',
})

const FileUpload = ({
    label, // Optional label for the file input
    value, // Current file value
    onChange, // Callback when file changes
    onUpload, // Callback when upload button is clicked
    accept, // Accepted file types (e.g., ".pdf,.doc,.docx")
    maxSize, // Maximum file size in bytes
    disabled = false, // Whether the input is disabled
    required = false, // Whether the field is required
    placeholder, // Placeholder text
    uploadButtonText, // Text for upload button
    chooseFileButtonText, // Text for choose file button
    showUploadButton = true, // Whether to show upload button
    showCloseIcon = true, // Whether to show close icon when file is selected
    onError, // Callback when validation error occurs
    ...rest
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const fileInputRef = useRef(null)
    const [selectedFile, setSelectedFile] = useState(value || null)
    const [error, setError] = useState(null)

    // Keep internal state in sync with external value
    useEffect(() => {
        setSelectedFile(value || null)
        // Clear error when value changes externally
        if (!value) {
            setError(null)
        }
    }, [value])

    const handleFileSelect = (event) => {
        const file = event.target.files[0]
        setError(null) // Clear any previous errors

        if (file) {
            // Comprehensive file validation
            const validation = validateFile(file)

            if (!validation.isValid) {
                setError(validation.error)
                setSelectedFile(null)
                if (onError) {
                    onError(validation.error)
                }
                // Clear the file input
                if (fileInputRef.current) {
                    try {
                        fileInputRef.current.value = ''
                    } catch (e) {}
                }
                return
            }

            setSelectedFile(file)
            if (onChange) {
                onChange(file)
            }
        }
    }

    // Comprehensive file validation function
    const validateFile = (file) => {
        // Check file size
        if (maxSize && file.size > maxSize) {
            const maxSizeMB = Math.round(maxSize / (1024 * 1024))
            const fileSizeMB = Math.round(file.size / (1024 * 1024))
            return {
                isValid: false,
                error: t('FileSizeExceedsLimit'),
            }
        }

        // Check if file is empty
        if (file.size === 0) {
            return {
                isValid: false,
                error: t('FileIsEmpty'),
            }
        }

        // Check file type if accept is specified
        if (accept) {
            const acceptedTypes = accept.split(',').map((type) => type.trim())
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
            const mimeType = file.type

            const isValidType = acceptedTypes.some((acceptedType) => {
                if (acceptedType.startsWith('.')) {
                    return acceptedType.toLowerCase() === fileExtension
                }
                return (
                    mimeType && mimeType.match(acceptedType.replace('*', '.*'))
                )
            })

            if (!isValidType) {
                return {
                    isValid: false,
                    error: t('InvalidFileType'),
                }
            }
        }

        return { isValid: true, error: null }
    }

    const handleChooseFile = () => {
        fileInputRef.current?.click()
    }

    const handleUpload = () => {
        if (selectedFile && onUpload) {
            onUpload(selectedFile)
        }
    }

    const handleClearFile = () => {
        // Don't allow clearing file if component is disabled
        if (disabled) {
            return
        }
        setSelectedFile(null)
        setError(null) // Clear any errors when clearing file
        if (fileInputRef.current) {
            try {
                fileInputRef.current.value = ''
            } catch (e) {}
        }
        if (onChange) {
            onChange(null)
        }
    }

    const getFileName = () => {
        if (selectedFile) {
            const fileName = selectedFile.name
            // Add tooltip for long file names
            return fileName
        }
        return placeholder ?? t('FileUploadPlaceholderKey')
    }

    return (
        <Box>
            {label && (
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: '600',
                        color: '#535b62',
                        marginBottom: '8px',
                        fontSize: '14px',
                    }}
                >
                    {label}
                    {required && (
                        <span style={{ color: '#D0021b', marginLeft: '4px' }}>
                            *
                        </span>
                    )}
                </Typography>
            )}

            <FileUploadContainer>
                <FileInputWrapper hasError={!!error}>
                    <GenericButton
                        onClick={handleChooseFile}
                        disabled={disabled}
                        rootStylesProp={{
                            backgroundColor: '#EFEFF1',
                            color: '#535b62',
                            border: 'none',
                            borderRadius: '0',
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            minWidth: 'auto',
                            height: '40px',
                            '&:hover': {
                                backgroundColor: '#EFEFF1',
                                color: '#535b62',
                            },
                        }}
                    >
                        {chooseFileButtonText || t('ChooseFile')}
                    </GenericButton>
                    <FileNameDisplay
                        sx={{
                            color: selectedFile
                                ? theme?.palette?.primary?.main || '#0057A0'
                                : '#5D6870',
                            paddingRight: selectedFile ? '8px' : '12px', // Less padding when close button is present
                        }}
                    >
                        <Tooltip
                            title={selectedFile ? selectedFile.name : ''}
                            placement="top"
                            arrow
                            disableHoverListener={
                                !selectedFile || selectedFile.name.length < 30
                            }
                        >
                            <Box
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {getFileName()}
                            </Box>
                        </Tooltip>
                    </FileNameDisplay>
                    {selectedFile && showCloseIcon && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '40px',
                                flexShrink: 0, // Prevent shrinking
                                paddingRight: '8px',
                            }}
                        >
                            <GenericButton
                                onClick={handleClearFile}
                                aria-label={t('RemoveFile')}
                                rootStylesProp={{
                                    backgroundColor: 'transparent',
                                    color: '#AAB0B4',
                                    minWidth: 'auto',
                                    width: '40px',
                                    height: '40px',
                                    padding: 0,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&&::before': { display: 'none' },
                                    '&&:hover::before': { display: 'none' },
                                    '&&:hover': {
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                        color: '#AAB0B4',
                                    },
                                    '&&:active': {
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                        opacity: 1,
                                    },
                                    '&&:focus': {
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                <Icon
                                    name="Close"
                                    style={{
                                        fontSize: '16px',
                                        color: 'inherit',
                                    }}
                                />
                            </GenericButton>
                        </Box>
                    )}
                </FileInputWrapper>

                {showUploadButton && (
                    <GenericButton
                        onClick={handleUpload}
                        disabled={disabled}
                        rootStylesProp={{
                            backgroundColor: '#0057A0',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            textTransform: 'none',
                            minWidth: 'auto',
                            height: '40px',
                            '&:hover': {
                                backgroundColor: '#004080',
                                color: '#ffffff',
                            },
                            '&:focus': {
                                backgroundColor: '#004080',
                                color: '#ffffff',
                            },
                            '&:disabled': {
                                backgroundColor: '#0057A0',
                                color: '#ffffff',
                                opacity: 0.6,
                            },
                        }}
                    >
                        {uploadButtonText || t('UploadFile')}
                    </GenericButton>
                )}

                <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileSelect}
                    disabled={disabled}
                    {...rest}
                />
            </FileUploadContainer>

            {error && (
                <Typography
                    sx={{
                        color: '#ff4d5f',
                        fontSize: '1.2rem',
                        marginTop: '0.5rem',
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    )
}

export default FileUpload
